import React, { useMemo } from 'react';
import { api, ShareLink, ShareLinkRecipientClient } from '../../services/api';
import { logSharableLinkAction } from '../../services/analytics';
import {
  SendToClientsAdapter,
  SendToClientsSheet,
} from '../../../shared/components/sendToClients/SendToClientsSheet';
import {
  clientMessageTemplates,
  fillClientMessage,
  shareLinkUrl,
  whatsAppChatUrl,
} from './shareLinkFormat';

const PLACEHOLDERS = [
  { token: '{{client_name}}', label: 'Client name' },
  { token: '{{agent_name}}', label: 'My name' },
  { token: '{{link}}', label: 'Link' },
];

/**
 * "Send to my clients" for a share link. Every client gets the link's one
 * shared URL, and each send is recorded against the link.
 */
export function ShareLinkToClientsSheet({ link, onClose }: {
  /** The link to send; null closes the sheet. */
  link: ShareLink | null;
  onClose: () => void;
}) {
  const url = link ? shareLinkUrl(link) : '';

  const adapter = useMemo((): SendToClientsAdapter<ShareLinkRecipientClient> => ({
    subtitle: `${link?.projectName || 'Share link'} · 3D Map`,
    load: () => (link ? api.getShareLinkRecipients(link.id) : Promise.resolve({ agentName: '', clients: [] })),
    templates: link ? clientMessageTemplates(link) : [],
    placeholders: PLACEHOLDERS,
    linkToken: url ? new RegExp(`\\{\\{\\s*link\\s*\\}\\}|${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i') : /\{\{\s*link\s*\}\}/i,
    preview: (message, client, agentName) => fillClientMessage(message, { clientName: client?.name ?? '', agentName, link: url }),
    prepare: async (client, message, agentName) => {
      const text = fillClientMessage(message, { clientName: client.name, agentName, link: url });
      return { href: whatsAppChatUrl(client.waNumber, text), text };
    },
    recordSend: async (client, text) => {
      if (!link) return undefined;
      const res = await api.recordShareLinkRecipient(link.id, { clientId: client.id, message: text });
      return { lastSentAt: res.lastSentAt };
    },
    onOpen: () => { if (link) logSharableLinkAction({ action: 'clients_open', link, source: 'card' }); },
    onSent: () => { if (link) logSharableLinkAction({ action: 'client_whatsapp', link, source: 'client_sheet' }); },
    emptyHint: 'Only Super Connector clients with a mobile number are listed here.',
    doneHint: 'Their leads will show on this link once they open it and verify.',
  }), [link, url]);

  return (
    <SendToClientsSheet
      open={!!link}
      resetKey={link ? String(link.id) : ''}
      adapter={adapter}
      onClose={onClose}
    />
  );
}
